import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useT } from '../../i18n/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../data/DataContext';
import { initialsOf } from '../../utils/format';

const SECTIONS = [
  {
    key: 'main',
    titleKey: 'nav.section_main',
    items: [{ to: '/dashboard', labelKey: 'nav.dashboard', icon: '⛪' }],
  },
  {
    key: 'management',
    titleKey: 'nav.section_management',
    items: [
      { to: '/members', labelKey: 'nav.members', icon: '👥', badgeKey: 'members', permission: 'members:view' },
      { to: '/events', labelKey: 'nav.events', icon: '🕊️', permission: 'events:view' },
      { to: '/reservations', labelKey: 'nav.reservations', icon: '🎟️', permission: 'reservations:view' },
      { to: '/transport', labelKey: 'nav.transport', icon: '🚌', permission: 'transport:view' },
      { to: '/payments', labelKey: 'nav.payments', icon: '💰', permission: 'payments:view' },
      { to: '/finance', labelKey: 'nav.finance', icon: '📒', permission: 'payments:view' },
      { to: '/payouts', labelKey: 'nav.payouts', icon: '🏦', permission: 'payouts:view' },
    ],
  },
  {
    key: 'analysis',
    titleKey: 'nav.section_analysis',
    items: [
      { to: '/reports', labelKey: 'nav.reports', icon: '📜', permission: 'reports:view' },
      { to: '/documents', labelKey: 'nav.documents', icon: '📝', permission: 'documents:view' },
      { to: '/audit', labelKey: 'nav.audit', icon: '🗂️', permission: 'audit:view' },
    ],
  },
  {
    key: 'admin',
    titleKey: 'nav.section_admin',
    items: [
      { to: '/users', labelKey: 'nav.users', icon: '⚙️', permission: 'users:view' },
      { to: '/roles', labelKey: 'nav.roles', icon: '🔐', permission: 'roles:view' },
      { to: '/settings', labelKey: 'nav.settings', icon: '🛠️', permission: 'settings:manage', role: 'admin' },
    ],
  },
];

export default function Sidebar({ isMobile, isOpen, onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const { user, logout, hasPermission, hasRole } = useAuth();
  const { members } = useData();

  const sidebarStyle = {
    width: '252px',
    background: 'var(--blue-deep)',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflowY: 'auto',
    color: 'white',
    position: isMobile ? 'fixed' : 'relative',
    top: isMobile ? 0 : 'auto',
    left: isMobile ? 0 : 'auto',
    zIndex: isMobile ? 120 : 'auto',
    transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-106%)') : 'none',
    transition: isMobile ? 'transform 0.22s ease' : 'none',
    boxShadow: isMobile && isOpen ? '0 16px 40px rgba(0,0,0,0.35)' : 'none',
    flexShrink: 0,
  };

  const handleNav = () => {
    if (isMobile && onClose) onClose();
  };

  const handleLogout = () => {
    logout();
    handleNav();
    navigate('/login', { replace: true });
  };

  const goProfile = () => {
    handleNav();
    navigate('/profile');
  };

  const badges = { members: members.length };

  return (
    <div style={sidebarStyle}>
      <div style={{ padding: '28px 22px 22px', borderBottom: '1px solid rgba(201,168,76,0.2)', textAlign: 'center' }}>
        <div style={{ fontSize: '36px', marginBottom: '6px', filter: 'drop-shadow(0 2px 8px rgba(201,168,76,0.4))' }}>✝</div>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: '13px', color: 'var(--gold-light)', lineHeight: '1.35', letterSpacing: '0.5px', marginBottom: '4px' }}>
          {t('app.name')}
        </div>
        <div style={{ fontFamily: "'EB Garamond', serif", fontStyle: 'italic', fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>
          {t('app.tagline')}
        </div>
      </div>

      {SECTIONS.map((section) => {
        const visible = section.items.filter((item) =>
          (!item.permission || hasPermission(item.permission)) && (!item.role || hasRole(item.role))
        );
        if (visible.length === 0) return null;
        return (
          <React.Fragment key={section.key}>
            <SectionLabel label={t(section.titleKey)} />
            {visible.map((item) => (
              <NavItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={t(item.labelKey)}
                badge={item.badgeKey ? badges[item.badgeKey] : undefined}
                onSelect={handleNav}
              />
            ))}
          </React.Fragment>
        );
      })}

      <div style={{ marginTop: 'auto', padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          type="button"
          onClick={goProfile}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            padding: 0,
            textAlign: 'left',
            marginBottom: '6px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 10px', borderRadius: '7px' }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Cinzel', serif", fontSize: '12px', fontWeight: '600',
              color: 'var(--blue-deep)', boxShadow: '0 2px 8px rgba(201,168,76,0.3)',
            }}>{user ? initialsOf(user.firstName, user.lastName) : '?'}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.78)', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user ? `${user.firstName} ${user.lastName}` : ''}
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>{t('nav.profile')}</div>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={handleLogout}
          style={{
            width: '100%',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.55)',
            cursor: 'pointer',
            padding: '7px 10px',
            borderRadius: '7px',
            fontFamily: "'EB Garamond', serif",
            fontSize: '13px',
          }}
        >
          {t('nav.logout')}
        </button>
      </div>
    </div>
  );
}

function SectionLabel({ label }) {
  return (
    <div style={{ padding: '16px 22px 6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
      <div style={{ fontFamily: "'Cinzel', serif", fontSize: '8.5px', color: 'rgba(201,168,76,0.5)', letterSpacing: '2.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
        {label}
      </div>
      <div style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
    </div>
  );
}

function NavItem({ to, icon, label, badge, onSelect }) {
  return (
    <NavLink
      to={to}
      onClick={onSelect}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 18px',
        cursor: 'pointer',
        color: isActive ? 'var(--gold-light)' : 'rgba(255,255,255,0.5)',
        fontFamily: "'EB Garamond', serif",
        fontSize: '14px',
        margin: '1px 10px',
        borderRadius: '7px',
        transition: 'all 0.18s',
        background: isActive ? 'rgba(201,168,76,0.14)' : 'transparent',
        textDecoration: 'none',
      })}
    >
      <span style={{ fontSize: '15px', width: '20px', textAlign: 'center' }}>{icon}</span>
      <span>{label}</span>
      {badge !== undefined && (
        <span style={{
          marginLeft: 'auto',
          background: 'rgba(201,168,76,0.25)',
          color: 'var(--gold-light)',
          fontFamily: "'DM Mono', monospace",
          fontSize: '10px',
          fontWeight: '500',
          padding: '1px 7px',
          borderRadius: '10px',
          border: '1px solid rgba(201,168,76,0.3)',
        }}>{badge}</span>
      )}
    </NavLink>
  );
}
